import { useUiText } from '../lib/ui-text';
/**
 * AdaptiveDialog - Unified responsive dialog component
 *
 * Desktop: Centered dialog with rounded corners and shadow
 * Mobile: DraggableBottomSheet with drag handle and close button
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';
import { DraggableBottomSheet } from './draggable-bottom-sheet';
import { ScrollArea } from './scroll-area';
import { Button } from './button';
import { cn } from '../lib/utils';

interface AdaptiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Additional class names for the content container */
  className?: string;
  /** Max width for desktop dialog (default: max-w-2xl) */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  /** Whether to show the header section (default: true) */
  showHeader?: boolean;
  /** Custom header content (replaces default title/description) */
  headerContent?: React.ReactNode;
  /** Snap points for mobile bottom sheet (default: [50, 90]) */
  snapPoints?: number[];
  /** Default snap index for mobile (default: 1 = 90%) */
  defaultSnapIndex?: number;
  /** Remove padding for full-bleed content on mobile (default: false) */
  fullBleed?: boolean;
  /** Fixed footer content rendered below the scrollable area */
  footer?: React.ReactNode;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
};

export function AdaptiveDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  maxWidth = '2xl',
  showHeader = true,
  headerContent,
  snapPoints = [50, 98],
  defaultSnapIndex = 0,
  fullBleed = false,
  footer,
}: AdaptiveDialogProps) {
  const uiText = useUiText();
  const isMobile = useIsMobile();
  const previouslyOpenRef = React.useRef(false);
  const restoreFocusRef = React.useRef<HTMLElement | null>(null);

  if (
    open &&
    !previouslyOpenRef.current &&
    typeof document !== 'undefined' &&
    document.activeElement instanceof HTMLElement
  ) {
    restoreFocusRef.current = document.activeElement;
  }
  previouslyOpenRef.current = open;

  // During SSR/hydration, render nothing to avoid hydration mismatch
  if (isMobile === undefined) {
    return null;
  }

  // Mobile: Use DraggableBottomSheet
  if (isMobile) {
    return (
      <DraggableBottomSheet
        open={open}
        onOpenChange={onOpenChange}
        snapPoints={snapPoints}
        defaultSnapIndex={defaultSnapIndex}
        className="rounded-t-3xl shadow-2xl border-t border-gray-200 dark:border-gray-700"
      >
        <div className="flex flex-col h-full">
          {/* Header with drag handle */}
          {showHeader && (
            <div
              className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800"
              data-drag-handle="true"
            >
              {headerContent ? (
                <div className="px-4 pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 pr-2">{headerContent}</div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onOpenChange(false)}
                      className="flex-shrink-0 h-8 w-8"
                      data-no-drag="true"
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">{uiText('close')}</span>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between px-4 pb-3">
                  <div className="flex-1 min-w-0 pr-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {title}
                    </h2>
                    {description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {description}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onOpenChange(false)}
                    className="flex-shrink-0 h-8 w-8"
                    data-no-drag="true"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">{uiText('close')}</span>
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Scrollable content */}
          {fullBleed ? (
            <div className={cn('flex-1 min-h-0 overflow-auto', className)}>{children}</div>
          ) : (
            <ScrollArea className={cn('flex-1 min-h-0', className)}>
              <div className="p-4">{children}</div>
            </ScrollArea>
          )}

          {/* Fixed footer */}
          {footer && (
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 p-4 bg-background">
              {footer}
            </div>
          )}
        </div>
      </DraggableBottomSheet>
    );
  }

  // Desktop: Use Dialog
  return (
    <DialogPrimitive.Root modal open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal
        container={typeof document !== 'undefined' ? document.body : undefined}
      >
        {/* Backdrop */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />

        {/* Card Content */}
        <DialogPrimitive.Content
          aria-modal="true"
          onCloseAutoFocus={event => {
            if (!restoreFocusRef.current) return;
            event.preventDefault();
            restoreFocusRef.current.focus();
          }}
          {...(!description ? { 'aria-describedby': undefined } : {})}
          className={cn(
            'fixed left-[50%] top-[50%] z-[9999]',
            'w-[95vw] translate-x-[-50%] translate-y-[-50%]',
            maxWidthClasses[maxWidth],
            'max-h-[85vh] overflow-hidden',
            'flex flex-col',
            'bg-white dark:bg-gray-900',
            'rounded-3xl shadow-2xl',
            'border border-gray-200 dark:border-gray-700',
            'duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          {/* Header */}
          {showHeader && (
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800">
              {headerContent ? (
                <div className="px-6 py-4">
                  <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
                  {description && (
                    <DialogPrimitive.Description className="sr-only">
                      {description}
                    </DialogPrimitive.Description>
                  )}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 pr-2">{headerContent}</div>
                    <DialogPrimitive.Close asChild>
                      <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8">
                        <X className="h-4 w-4" />
                        <span className="sr-only">{uiText('close')}</span>
                      </Button>
                    </DialogPrimitive.Close>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between px-6 py-4">
                  <div className="flex-1 min-w-0 pr-4">
                    <DialogPrimitive.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {title}
                    </DialogPrimitive.Title>
                    {description && (
                      <DialogPrimitive.Description className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {description}
                      </DialogPrimitive.Description>
                    )}
                  </div>
                  <DialogPrimitive.Close asChild>
                    <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8">
                      <X className="h-4 w-4" />
                      <span className="sr-only">{uiText('close')}</span>
                    </Button>
                  </DialogPrimitive.Close>
                </div>
              )}
            </div>
          )}

          {/* Scrollable content */}
          <div
            className={cn(
              'flex-1 min-h-0 overflow-y-auto',
              'scrollbar-thin scrollbar-track-transparent',
              'scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400',
              'dark:scrollbar-thumb-gray-600 dark:hover:scrollbar-thumb-gray-500',
              className
            )}
          >
            <div className="p-6">{children}</div>
          </div>

          {/* Fixed footer */}
          {footer && (
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 px-6 py-4">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
